/*
 * Copyright (c) 2017 Datarista, Inc.
 */

import { createStore, applyMiddleware, combineReducers, compose } from 'redux';
import { browserHistory } from 'react-router';
import { routerMiddleware, routerReducer } from 'react-router-redux';
import thunk from 'redux-thunk';
import createLogger from 'redux-logger';

// 3rd Party Reducers
import { reducer as formReducer } from 'redux-form';

// Native Reducers
import accountReducer from '../reducers/account';
import accountSettingsReducer from '../reducers/accountSettings';
import dialogReducer from '../reducers/dialog';
import orderReducer from '../reducers/order';
import authenticationReducer from '../reducers/authentication';
import errorReducer from '../reducers/error';
import progressReducer from '../reducers/progress';
import dataReducer from '../reducers/data';
import reportsReducer from '../reducers/reports';
import successReducer from '../reducers/success';
import superadminReducer from '../reducers/superadmin';
import platformUseCaseReducer from '../reducers/platformUseCase';
import pricingReducer from '../reducers/pricing';
import {
  userAuthReducer,
  userListReducer,
  userInviteReducer,
  // userTypeListReducer,
} from '../reducers/user';

// Native Customer Portal Reducers
import customerPortalReducter from '../reducers/customerPortal';

function configureLogger() {
  // bypass logger middleware altogether in test & prod:
  const dummyLogger = () => next => action => {
    next(action);
  }; // no-op middlware
  if (typeof process !== 'undefined' && ['production', 'test'].includes(process.env.NODE_ENV)) {
    return dummyLogger;
  }

  // take care of non-browser environments: https://github.com/fcomb/redux-logger#logger-object
  const logger = typeof window !== 'undefined' ? window.console : console;

  // filter actions we want to ignore:
  const predicate = (getState, action) =>
    !['redux-form/FOCUS', 'redux-form/BLUR', 'redux-form/CHANGE', 'redux-form/DESTROY'].includes(
      action.type
    );

  // collapse these log messages
  const collapsed = (getState, action) =>
    [
      'ACTION_STARTED',
      'ACTION_STARTED_IGNORE_SPINNER',
      'ACTION_COMPLETED',
      '@@router/LOCATION_CHANGE',
    ].includes(action.type);

  return createLogger({ logger, collapsed, predicate });
}

const reducer = combineReducers({
  account: accountReducer,
  accountSettings: accountSettingsReducer,
  dialog: dialogReducer,
  orderList: orderReducer,
  authentication: authenticationReducer,
  error: errorReducer,
  form: formReducer,
  progress: progressReducer,
  routing: routerReducer,
  data: dataReducer,
  reports: reportsReducer,
  success: successReducer,
  superadmin: superadminReducer,
  pricing: pricingReducer,
  user: userAuthReducer,
  userList: userListReducer,
  userInvite: userInviteReducer,
  // userTypeList: userTypeListReducer,
  platformUseCaseMap: platformUseCaseReducer,
  priceBook: pricingReducer,

  // Customer Portal Reducers
  customerPortalData: customerPortalReducter,
});

const historyMiddleware = routerMiddleware(browserHistory);

// Second argument of the create store function is a persisted state (from a refresh)
const store = compose(applyMiddleware(thunk, historyMiddleware, configureLogger()))(createStore)(
  reducer /* , persistedState */
);

export default store;
